import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Grid,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import { webhookService, WebhookSettings } from '../services/webhookService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Settings: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [webhookSettings, setWebhookSettings] = useState<WebhookSettings>({
    url: '',
    isActive: false,
    configured: false
  });
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookActive, setWebhookActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    loadWebhookSettings();
  }, []);

  const loadWebhookSettings = async () => {
    try {
      setLoading(true);
      const settings = await webhookService.getWebhookSettings();
      setWebhookSettings(settings);
      setWebhookUrl(settings.url);
      setWebhookActive(settings.isActive);
    } catch (error) {
      console.error('Failed to load webhook settings:', error);
      showNotification('Webhook設定の読み込みに失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWebhookSettings = async () => {
    try {
      setSaving(true);

      // Validate URL
      const validation = webhookService.validateWebhookUrl(webhookUrl);
      if (!validation.isValid) {
        showNotification(validation.error || 'Invalid URL', 'error');
        return;
      }

      const updatedSettings = await webhookService.saveWebhookSettings(webhookUrl, webhookActive);
      setWebhookSettings(updatedSettings);
      showNotification('Webhook設定が保存されました', 'success');
    } catch (error) {
      console.error('Failed to save webhook settings:', error);
      showNotification('Webhook設定の保存に失敗しました', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTestWebhook = async () => {
    try {
      setTesting(true);
      if (!webhookSettings.configured || !webhookSettings.isActive) {
        showNotification('アクティブなWebhook設定がありません', 'error');
        return;
      }

      await webhookService.testWebhookConnection();
      showNotification('テストWebhookが送信されました', 'success');
    } catch (error) {
      console.error('Failed to test webhook:', error);
      showNotification('Webhookテストに失敗しました', 'error');
    } finally {
      setTesting(false);
    }
  };

  const showNotification = (message: string, severity: 'success' | 'error') => {
    setNotification({ open: true, message, severity });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        設定
      </Typography>

        <Paper sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="システム設定" />
              <Tab label="セキュリティ" />
              <Tab label="通知設定" />
            </Tabs>
          </Box>

          {/* System Settings Tab */}
          <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" gutterBottom>
              システム設定
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="システム名"
                  defaultValue="GCペイメントシステム"
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="管理者メールアドレス"
                  defaultValue="admin@globalcast.jp"
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="サポートメールアドレス"
                  defaultValue="support@globalcast.jp"
                  margin="normal"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl component="fieldset" sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="メンテナンスモード"
                  />
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="デバッグログ有効"
                  />
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="自動バックアップ"
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3 }}>
              <Button variant="contained">
                設定を保存
              </Button>
            </Box>
          </TabPanel>

          {/* Security Tab */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" gutterBottom>
              セキュリティ設定
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              セキュリティ設定の変更は慎重に行ってください。
            </Alert>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="セッションタイムアウト（分）"
                  type="number"
                  defaultValue="30"
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="パスワード最小長"
                  type="number"
                  defaultValue="8"
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="ログイン試行回数制限"
                  type="number"
                  defaultValue="5"
                  margin="normal"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl component="fieldset" sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="二要素認証必須"
                  />
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="IP制限有効"
                  />
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="API レート制限"
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3 }}>
              <Button variant="contained">
                セキュリティ設定を保存
              </Button>
            </Box>
          </TabPanel>

          {/* Notifications Tab */}
          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" gutterBottom>
              通知設定
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              システムイベントの通知設定を管理できます。
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle1" gutterBottom>
                  メール通知
                </Typography>
                <FormControl component="fieldset">
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="決済完了通知"
                  />
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="決済失敗通知"
                  />
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="サブスクリプション更新通知"
                  />
                  <FormControlLabel
                    control={<Switch />}
                    label="システムメンテナンス通知"
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Webhook通知
                </Typography>
                {loading ? (
                  <Box display="flex" alignItems="center" gap={1} sx={{ mt: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">設定を読み込み中...</Typography>
                  </Box>
                ) : (
                  <>
                    <TextField
                      fullWidth
                      label="Webhook URL"
                      placeholder="https://your-app.com/webhooks"
                      value={webhookUrl}
                      onChange={(e) => setWebhookUrl(e.target.value)}
                      margin="normal"
                      helperText="全ての決済イベントがこのURLに送信されます"
                    />
                    <FormControl component="fieldset" sx={{ mt: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={webhookActive}
                            onChange={(e) => setWebhookActive(e.target.checked)}
                          />
                        }
                        label="Webhook有効"
                      />
                    </FormControl>
                    
                    {webhookSettings.configured && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        最終更新: {webhookSettings.updatedAt ? new Date(webhookSettings.updatedAt).toLocaleString() : 'N/A'}
                      </Alert>
                    )}
                    
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <Button 
                        variant="contained" 
                        onClick={handleSaveWebhookSettings}
                        disabled={saving || !webhookUrl.trim()}
                      >
                        {saving ? <CircularProgress size={20} /> : 'Webhook設定を保存'}
                      </Button>
                      
                      {webhookSettings.configured && webhookSettings.isActive && (
                        <Button 
                          variant="outlined" 
                          onClick={handleTestWebhook}
                          disabled={testing}
                        >
                          {testing ? <CircularProgress size={20} /> : 'テスト送信'}
                        </Button>
                      )}
                    </Box>
                  </>
                )}
              </Grid>
            </Grid>
          </TabPanel>
        </Paper>
        
        {/* Notification Snackbar */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={() => setNotification({ ...notification, open: false })}
        >
          <Alert 
            onClose={() => setNotification({ ...notification, open: false })} 
            severity={notification.severity}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
  );
};

export default Settings;